import { BaseService } from './BaseService';
import { apiClient } from '../Api';
import { AutocompleteResult, PaginatedSearchResponse, SearchFilters } from '../types';

class SearchService extends BaseService {
  constructor() {
    super('/api/search');
  }

  async autocomplete(query: string, size: number = 5, signal?: AbortSignal): Promise<AutocompleteResult[]> {
    try {
      const data = await this.get<any[]>(`/autocomplete?query=${encodeURIComponent(query)}&size=${size}`, { signal });
      if (!Array.isArray(data)) {
        return [];
      }
      return data.map((item: any) => ({
        officialTitle: item.officialTitle || item.OfficialTitle || '',
        highlight: item.highlight || item.Highlight || undefined,
      }));
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('SearchService.autocomplete error', error);
      return [];
    }
  }

  async getDocumentYears(signal?: AbortSignal): Promise<number[]> {
    try {
      const data = await apiClient.get<number[]>('/api/documents/years', { signal });
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('SearchService.getDocumentYears error', error);
      }
      return [];
    }
  }

  async getFiltersData(signal?: AbortSignal): Promise<{ natures: string[], domains: string[], origins: string[] }> {
    try {
      const data = await this.get<any>('/filters-data', { signal });
      return {
        natures: data.natures || data.Natures || [],
        domains: data.domains || data.Domains || [],
        origins: data.origins || data.Origins || []
      };
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('SearchService.getFiltersData error', error);
      }
      return { natures: [], domains: [], origins: [] };
    }
  }

  async globalSearch(
    query: string,
    filters: SearchFilters,
    page: number = 1,
    size: number = 5,
    signal?: AbortSignal,
    searchOrigin: string = 'GLOBAL_SEARCH'
  ): Promise<PaginatedSearchResponse> {
    let visitorId: string | undefined;
    try {
      const { getOrCreateVisitorId } = await import('../utils/visitorTracker');
      visitorId = getOrCreateVisitorId();
    } catch {
      // ignore
    }

    const body = {
      query,
      page,
      size,
      issuingAuthority: filters.issuingAuthority || null,
      legalStatus: filters.legalStatus || null,
      effectiveYear: filters.effectiveYear || null,
      categoryName: filters.nature || null,
      legalOrigin: filters.legalOrigin || null,
      areaOfLaw: filters.areaOfLaw || null,
      keywords: filters.keywords ? filters.keywords.join(' ') : null,
      visitorId,
      searchOrigin
    };

    const raw = await this.post<any>('/global', body, { signal });

    return {
      total: raw.Total ?? raw.total ?? 0,
      page: raw.Page ?? raw.page ?? page,
      size: raw.Size ?? raw.size ?? size,
      totalPages: raw.TotalPages ?? raw.totalPages ?? 0,
      hasMore: raw.HasMore ?? raw.hasMore ?? false,
      data: (raw.Data ?? raw.data ?? []).map((item: any) => {
        const bm = item.BestMatch ?? item.bestMatch ?? null;
        return {
          id: item.Id ?? item.id ?? '',
          officialTitle: item.OfficialTitle ?? item.officialTitle ?? '',
          titleHighlight: item.TitleHighlight ?? item.titleHighlight ?? null,
          referenceNumber: item.ReferenceNumber ?? item.referenceNumber ?? null,
          categoryName: item.CategoryName ?? item.categoryName ?? null,
          dateOfAdoption: item.DateOfAdoption ?? item.dateOfAdoption ?? null,
          effectiveYear: item.EffectiveYear ?? item.effectiveYear ?? null,
          areaOfLaw: item.AreaOfLaw ?? item.areaOfLaw ?? null,
          legalOrigin: item.LegalOrigin ?? item.legalOrigin ?? null,
          legalStatus: item.LegalStatus ?? item.legalStatus ?? null,
          issuingAuthority: item.IssuingAuthority ?? item.issuingAuthority ?? null,
          totalPages: item.TotalPages ?? item.totalPages ?? null,
          score: item.Score ?? item.score ?? null,
          isLocked: item.IsLocked ?? item.isLocked ?? false,
          hasSummary: item.HasSummary ?? item.hasSummary ?? false,
          hasJurisdiction: item.HasJurisdiction ?? item.hasJurisdiction ?? false,
          hasDate: item.HasDate ?? item.hasDate ?? false,
          hasPdf: item.HasPdf ?? item.hasPdf ?? false,
          bestMatch: bm ? {
            articleNumber: bm.ArticleNumber ?? bm.articleNumber ?? null,
            chunkType: bm.ChunkType ?? bm.chunkType ?? null,
            pages: bm.Pages ?? bm.pages ?? [],
            highlight: bm.Highlight ?? bm.highlight ?? null,
          } : null,
        };
      })
    };
  }

  async trackSearch(data: {
    query: string;
    totalResults?: number;
    executionTimeMs?: number;
    searchOrigin?: string;
    filtersApplied?: string;
    page?: number;
  }): Promise<void> {
    if (!data || !data.query || !data.query.trim()) {
      return;
    }

    try {
      let visitorId: string | undefined;
      try {
        const { getOrCreateVisitorId } = await import('../utils/visitorTracker');
        visitorId = getOrCreateVisitorId();
      } catch {
        // ignore
      }

      await this.post('/track', {
        query: data.query,
        totalResults: data.totalResults ?? 1,
        executionTimeMs: data.executionTimeMs ?? 0,
        visitorId,
        searchOrigin: data.searchOrigin ?? 'AUTOCOMPLETE_CLICK',
        filtersApplied: data.filtersApplied,
        page: data.page ?? 1
      });
    } catch (err) {
      // Les erreurs de tracking sont silencieuses
    }
  }
}

export const searchService = new SearchService();
